import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardHeader } from './DashboardHeader';
import { Card } from './ui/card';
import { PitchSummary } from './PitchSummary';
import ReactMarkdown from 'react-markdown';
import { AnalysisNav } from './ui/analysis-nav';
import { BackgroundPattern } from '@/components/ui/background-pattern';
import { StarsBackground } from './ui/stars-background';
import { 
    Lightbulb, 
    BarChart3, 
    Users, 
    TrendingUp, 
    Award, 
    AlertTriangle, 
    Milestone, 
    DollarSign,
    Rocket,
    Target,
    LineChart,
    Clock,
    Briefcase,
    Trophy,
    ShieldCheck,
    Layers,
    Zap,
    Building,
    Cpu
} from 'lucide-react';
import { motion } from "framer-motion";
import { fadeIn, slideIn } from "../utils/motion";
import { trackFeatureUsage } from '../analytics/posthog';

interface TeamMember {
    name: string;
    role: string;
    key_accomplishments: string;
}

interface AnalysisSection {
    vision_and_value_prop: {
        vision_clarity: string;
        unique_value_prop: string;
        defensibility: string;
    };
    market_opportunity: {
        market_size: string;
        market_timing: string;
        market_trends: string;
    };
    problem_analysis: {
        problem_definition: string;
        problem_scale: string;
        target_audience: string;
        validation: string;
    };
    solution_and_tech: {
        problem_solution_fit: string;
        competitive_advantage: string;
        technical_feasibility: string;
        scalability: string;
    };
    business_model: {
        revenue_model: string;
        unit_economics: string;
        go_to_market: string;
        customer_acquisition: string;
    };
    team_and_execution: {
        founding_team: TeamMember[];
        team_assessment: string;
        unique_insights: string;
        progress_to_date: string;
    };
    competition_and_moat: {
        key_competitors: string[];
        competitive_advantages: string;
        defensibility_analysis: string;
    };
    investment_potential: {
        key_risks: string[];
        key_opportunities: string[];
        value_creation_milestones: string[];
        return_profile: string;
    };
}

interface AnalysisData {
    id: number;
    results?: {
        sections: AnalysisSection;
        interview_questions: any;
    };
}

const Analysis: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { api, isAuthenticated } = useAuth();
    const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                const response = await api.get(`/api/analysis/${id}`);
                setAnalysis(response.data);
                setError(null);
                
                // Track analysis view
                if (response.data) {
                    trackFeatureUsage('pitch_analysis', 'view', {
                        analysis_id: id,
                        company_name: response.data.company_name,
                        has_summary: !!response.data.summary,
                        has_team_analysis: !!response.data.team_analysis,
                        has_financial_analysis: !!response.data.financial_analysis
                    });
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to fetch analysis');
                console.error('Error fetching analysis:', err);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchAnalysis();
        }
    }, [api, id, isAuthenticated]);

    const [activeTab, setActiveTab] = useState('analysis');
    const navigate = useNavigate();

    useEffect(() => {
        if (activeTab === 'interview') {
            navigate(`/interview/${id}`);
        }
    }, [activeTab, id, navigate]);

    const renderContent = () => {
        if (loading) {
            return <div className="text-center mt-8">Loading...</div>;
        }

        if (error) {
            return <div className="text-center mt-8 text-red-500">{error}</div>;
        }

        if (!analysis?.results?.sections) {
            return <div className="text-center mt-8">No analysis results available.</div>;
        }

        const sections = analysis.results.sections;

        return (
            <div className="space-y-16">
                {/* Vision and Value Proposition */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.3, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                                <Rocket className="mr-2 text-blue-500" size={26} />
                                Vision & Value Proposition
                            </h2>
                            <div className="space-y-4 text-left">
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Lightbulb className="mr-2 text-yellow-500" size={18} />
                                        Vision Clarity
                                    </h3>
                                    <p className="text-gray-700">{sections.vision_and_value_prop.vision_clarity}</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Zap className="mr-2 text-purple-500" size={18} />
                                        Unique Value Proposition
                                    </h3>
                                    <p className="text-gray-700">{sections.vision_and_value_prop.unique_value_prop}</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <ShieldCheck className="mr-2 text-green-500" size={18} />
                                        Defensibility
                                    </h3>
                                    <p className="text-gray-700">{sections.vision_and_value_prop.defensibility}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Market Opportunity */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.5, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                                <BarChart3 className="mr-2 text-green-500" size={26} />
                                Market Opportunity
                            </h2>
                            <div className="space-y-4 text-left">
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Building className="mr-2 text-blue-500" size={18} />
                                        Market Size
                                    </h3>
                                    <p className="text-gray-700">{sections.market_opportunity.market_size}</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Clock className="mr-2 text-orange-500" size={18} />
                                        Market Timing
                                    </h3>
                                    <p className="text-gray-700">{sections.market_opportunity.market_timing}</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <LineChart className="mr-2 text-purple-500" size={18} />
                                        Market Trends
                                    </h3>
                                    <p className="text-gray-700">{sections.market_opportunity.market_trends}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Team & Execution */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.7, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                                <Users className="mr-2 text-indigo-500" size={26} />
                                Team & Execution
                            </h2>
                            <div className="space-y-6 text-left">
                                <div>
                                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                                        <Award className="mr-2 text-yellow-500" size={18} />
                                        Founding Team
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {sections.team_and_execution.founding_team.map((member: TeamMember, index: number) => (
                                            <div key={index} className="p-4 bg-white/5 rounded-lg">
                                                <h4 className="font-medium text-primary">{member.name}</h4>
                                                <p className="text-sm text-gray-600">{member.role}</p>
                                                {member.key_accomplishments && (
                                                    <p className="text-sm text-gray-500 mt-2">{member.key_accomplishments}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Briefcase className="mr-2 text-blue-500" size={18} />
                                        Team Assessment
                                    </h3>
                                    <p className="text-gray-700">{sections.team_and_execution.team_assessment}</p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Trophy className="mr-2 text-amber-500" size={18} />
                                        Progress to Date
                                    </h3>
                                    <p className="text-gray-700">{sections.team_and_execution.progress_to_date}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Investment Potential */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.9, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                                <DollarSign className="mr-2 text-emerald-500" size={26} />
                                Investment Potential
                            </h2>
                            <div className="space-y-4 text-left">
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <AlertTriangle className="mr-2 text-red-500" size={18} />
                                        Key Risks
                                    </h3>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {sections.investment_potential.key_risks.map((risk: string, index: number) => (
                                            <li key={index} className="text-gray-700">{risk}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <TrendingUp className="mr-2 text-green-500" size={18} />
                                        Key Opportunities
                                    </h3>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {sections.investment_potential.key_opportunities.map((opportunity: string, index: number) => (
                                            <li key={index} className="text-gray-700">{opportunity}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Milestone className="mr-2 text-blue-500" size={18} />
                                        Value Creation Milestones
                                    </h3>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {sections.investment_potential.value_creation_milestones.map((milestone: string, index: number) => (
                                            <li key={index} className="text-gray-700">{milestone}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Layers className="mr-2 text-purple-500" size={18} />
                                        Return Profile
                                    </h3>
                                    <p className="text-gray-700">{sections.investment_potential.return_profile}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>
            </div>
        );
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!analysis?.results) {
        return <div>No analysis data available</div>;
    }

    return (
        <div className="min-h-screen bg-background relative overflow-x-hidden">
            {/* Stars Background */}
            <div className="fixed inset-0 -z-10">
                <StarsBackground />
            </div>
            
            {/* Content */}
            <div className="relative z-10">
                {/* DashboardHeader is already included in the ProtectedRoute wrapper */}
                <AnalysisNav id={id || ''} />
                <motion.div 
                    variants={slideIn("left", "tween", 0.2, 1)}
                    initial="hidden"
                    animate="show"
                    className="max-w-6xl mx-auto px-4 py-8"
                >
                    {renderContent()}
                </motion.div>
            </div>
        </div>
    );
};

export default Analysis;
